import type { CostRecord } from './costModel';

/** A lost response may hide a committed write. Keep its complete request, not
 * just the key: timestamps, attribution, evidence and FX must replay together. */
export function createCostCaptureMutation(idempotencyKey: string) {
  let busy = false;
  let pending: Record<string, unknown> | null = null;
  let saved: CostRecord | null = null;
  return {
    get busy() { return busy; },
    get uncertain() { return pending !== null && !busy; },
    get saved() { return saved; },
    async save(build: () => Record<string, unknown>, send: (payload: Record<string, unknown>) => Promise<unknown>, invalidMessage: string) {
      if (busy) return null;
      if (saved) return saved;
      const payload = pending ?? JSON.parse(JSON.stringify({ ...build(), idempotencyKey })) as Record<string, unknown>;
      pending = payload; busy = true;
      try {
        const response = await send(payload) as { cost?: CostRecord } | null;
        if (!response?.cost || typeof response.cost.id !== 'string' || !response.cost.id) throw new Error(invalidMessage);
        saved = response.cost; pending = null;
        return saved;
      } catch (error) {
        const status = Number((error as { status?: unknown })?.status);
        // An explicit HTTP rejection permits correction with the same identity.
        // Timeout, network/5xx and malformed success leave the outcome uncertain.
        if (status >= 400 && status < 500 && status !== 408) pending = null;
        throw error;
      } finally { busy = false; }
    },
  };
}
